import { PostamatPointModel } from "api";
import actionTypes from "constants/map";
import { load, store } from "helpers/filtersStorage";
import { RegionDryingPeriod, RoadRestrictionModel, RoadRestrictionTypesModel } from "helpers/models";
import { PaymentPointModel } from "helpers/models/paymentPoint";
import { PlatonPointModel } from "helpers/models/platonPoint";
import { Action, handleActions } from "redux-actions";

const {
    SWITCH_MAP,
    MERGE_MAP_VIEWPORT,
    MERGE_MAP_DATA,
    MERGE_PARAMS,
    CHANGE_CENTER,
    MERGE_MAP_HEADER,
    GET_PLATON_POINTS,
    GET_POSTAMAT_POINTS,
    GET_PAYMENT_POINTS,
    GET_ROAD_RESTRICTIONS,
    GET_ROAD_RESTRICTION_TYPES,
    SET_REGION_DRYING_PERIODS,
} = actionTypes;

const initialState: State = {
    mode: "main",
    params: load("map", {}),
    platonPoints: [],
    postamatPoints: [],
    paymentPoints: [],
    roadRestrictions: {},
    roadRestrictionTypes: [],
    regionDryingPeriods: [],
    data: {
        items: [],
        callback: undefined,
        currentBid: undefined,
        bidLastCarLocation: undefined,
        carsLoadedAt: undefined,
        carsLastLocationsRequestSentAt: undefined,
        carStopsLoadedAt: undefined,
        renderCarMarkers: false,
    },
    header: {},
    viewport: {
        zoom: 3,
        center: [55.7473, 52.4568],
    },
};

type State = {
    mode: "main" | "picker";
    params: {};
    platonPoints: PlatonPointModel[];
    postamatPoints: PostamatPointModel[];
    paymentPoints: PaymentPointModel[];
    roadRestrictions: { [key: number]: RoadRestrictionModel[] };
    roadRestrictionTypes: RoadRestrictionTypesModel[];
    regionDryingPeriods: RegionDryingPeriod[];
    data: {
        items?: {}[];
        callback?: (value: {}) => void;
        currentBid?: string;
        bidLastCarLocation?: {};
        carsLoadedAt?: number;
        carsLastLocationsRequestSentAt?: number;
        carStopsLoadedAt?: number;
        renderCarMarkers?: boolean;
    };
    header: {};
    viewport: {
        zoom: number;
        center: [number, number];
    };
};

type SwitchPayload = { mode: "main" | "picker"; callback: (value: {}) => void; value: {}; withoutRadius?: boolean };
type MergeMapHeaderPayload = { compareBidId: string };
type MergeMapDataPayload = { compareBidId: string; carsLastLocationsRequestSentAt: number };
type ChangeCenterPayload = [number, number];
type GetPlatonPointsPayload = PlatonPointModel[];
type GetPostamatPointsPayload = PostamatPointModel[];
type GetPaymentPointsPayload = PaymentPointModel[];
type GetRoadRestrictionsPayload = RoadRestrictionModel[];
type GetRoadRestrictionTypesPayload = RoadRestrictionTypesModel[];
type SetRegionDryingPeriodsPayload = RegionDryingPeriod[];

type MergeParamsPayload = {}[];

type CombinedPayload =
    | SwitchPayload
    | MergeMapHeaderPayload
    | MergeMapDataPayload
    | ChangeCenterPayload
    | GetPlatonPointsPayload
    | GetPostamatPointsPayload
    | GetPaymentPointsPayload
    | GetRoadRestrictionsPayload
    | GetRoadRestrictionTypesPayload
    | SetRegionDryingPeriodsPayload;

export default handleActions<State, CombinedPayload>(
    {
        [SWITCH_MAP]: (state, { payload: { mode, callback, value, withoutRadius } }: Action<SwitchPayload>) => ({
            ...state,
            mode,
            data: { ...state.data, callback, value, withoutRadius },
        }),
        [MERGE_MAP_VIEWPORT]: (state, { payload }: Action<{}>) => ({
            ...state,
            viewport: { ...state.viewport, ...payload },
        }),
        [MERGE_MAP_HEADER]: (state, { payload: { compareBidId, ...payload } }: Action<MergeMapHeaderPayload>) => {
            if (compareBidId && (compareBidId !== state.data.currentBid || !state.data.currentBid)) {
                return state;
            } else {
                return { ...state, header: payload };
            }
        },
        [MERGE_MAP_DATA]: (state, { payload: { compareBidId, ...payload } }: Action<MergeMapDataPayload>) => {
            const { carsLastLocationsRequestSentAt } = payload;
            if (
                (compareBidId && (compareBidId !== state.data.currentBid || !state.data.currentBid)) ||
                (carsLastLocationsRequestSentAt &&
                    state.data.carsLastLocationsRequestSentAt &&
                    carsLastLocationsRequestSentAt < state.data.carsLastLocationsRequestSentAt)
            ) {
                return state;
            }
            return {
                ...state,
                data: { ...state.data, ...payload },
            };
        },
        [CHANGE_CENTER]: (state, { payload }: Action<ChangeCenterPayload>) => ({
            ...state,
            viewport: { ...state.viewport, center: payload, zoom: state.viewport.zoom > 15 ? state.viewport.zoom : 15 },
        }),
        [GET_PLATON_POINTS]: (state, { payload }: Action<GetPlatonPointsPayload>) => ({
            ...state,
            platonPoints: payload,
        }),
        [GET_ROAD_RESTRICTIONS]: (state, { payload }: Action<GetRoadRestrictionsPayload>) => ({
            ...state,
            roadRestrictions: Object.groupBy(payload, (item: RoadRestrictionModel) => item.type?.id) as {
                [key: number]: RoadRestrictionModel[];
            },
        }),
        [GET_ROAD_RESTRICTION_TYPES]: (state, { payload }: Action<GetRoadRestrictionTypesPayload>) => ({
            ...state,
            roadRestrictionTypes: payload,
        }),
        [SET_REGION_DRYING_PERIODS]: (state, { payload }: Action<SetRegionDryingPeriodsPayload>) => ({
            ...state,
            regionDryingPeriods: payload,
        }),
        [GET_POSTAMAT_POINTS]: (state, { payload }: Action<GetPostamatPointsPayload>) => ({
            ...state,
            postamatPoints: payload || [],
        }),
        [GET_PAYMENT_POINTS]: (state, { payload }: Action<GetPaymentPointsPayload>) => ({
            ...state,
            paymentPoints: payload || [],
        }),
        [MERGE_PARAMS]: (state, { payload }: Action<MergeParamsPayload>) => {
            store("map", payload);
            return {
                ...state,
                params: payload,
            };
        },
    },
    initialState,
);
